import React, { createContext, useContext, useState, ReactNode } from "react";
import { FileLocation } from "../types/fileUploadTypes";
import { Status } from "../enums/Status";
import { FileOptionTabTitle } from "../types/fileUploadTypes";

export interface UploadedFiles {
  [FileOptionTabTitle.PORTFOLIO]: UploadedPortfolio;
  [FileOptionTabTitle.ENGAGEMENT_RECORDS]: UploadedEngagementRecords;
  [FileOptionTabTitle.VOTING_RECORDS]: UploadedVotingRecords;
}

type UploadedPortfolio = {
  file: File | null;
  status: Status;
  location: FileLocation | null;
};

type UploadedEngagementRecords = {
  file: File | null;
  status: Status;
  location: FileLocation | null;
};

type UploadedVotingRecords = {
  file: File | null;
  status: Status;
  location: FileLocation | null;
};

const DEFAULT_UPLOADED_FILES: UploadedFiles = {
  [FileOptionTabTitle.PORTFOLIO]: {
    file: null,
    status: Status.PENDING,
    location: null,
  },
  [FileOptionTabTitle.ENGAGEMENT_RECORDS]: {
    file: null,
    status: Status.PENDING,
    location: null,
  },
  [FileOptionTabTitle.VOTING_RECORDS]: {
    file: null,
    status: Status.PENDING,
    location: null,
  },
};

interface UploadedFilesContextType {
  uploadedFiles: UploadedFiles;
  setUploadedFiles: React.Dispatch<React.SetStateAction<UploadedFiles>>;
  resetUploadedFiles: () => void;
  deleteFile: (fileType: FileOptionTabTitle) => void;
}

const UploadedFilesContext = createContext<
  UploadedFilesContextType | undefined
>(undefined);

export const useUploadedFiles = (): UploadedFilesContextType => {
  const context = useContext(UploadedFilesContext);
  if (context === undefined) {
    throw new Error(
      "useUploadedFiles must be used within an uploadedFilesProvider"
    );
  }
  return context;
};

export const UploadedFilesProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFiles>(
    DEFAULT_UPLOADED_FILES
  );
  const resetUploadedFiles = (): void => {
    setUploadedFiles(DEFAULT_UPLOADED_FILES);
  };

  const deleteFile = (fileType: FileOptionTabTitle): void => {
    setUploadedFiles((prevUploadedFiles: UploadedFiles) => ({
      ...prevUploadedFiles,
      [fileType]: {
        file: null,
        status: Status.PENDING,
        location: null,
      },
    }));
  };
  return (
    <UploadedFilesContext.Provider
      value={{
        uploadedFiles,
        setUploadedFiles,
        resetUploadedFiles,
        deleteFile,
      }}
    >
      {children}
    </UploadedFilesContext.Provider>
  );
};
